import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaChevronDown, FaTimes, FaSearch, FaSortAmountDown, FaFilter, FaCheck, FaTag } from 'react-icons/fa';
import { loadAllModelSpecs } from '../../utils/modelSchemaLoader';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AIModelSelector Component - Redesigned
 * 
 * Features:
 * - Sleek, dark-themed, glassmorphic modal
 * - Keyword filter pills (Google, Reve, Photorealistic, Low cost)
 * - Sorting by Cost (Context-aware) and Vendor
 * - Full price display
 * - Responsive grid layout
 */

const FALLBACK_LOGO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23888'%3E%3Cpath d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z'/%3E%3C/svg%3E";

/**
 * Helper function to format unit_cost or min_cost for display
 * Handles both numeric and object types
 */
const formatCostForDisplay = (cost) => {
  if (cost === undefined || cost === null) return 'N/A';
  
  if (typeof cost === 'number') {
    return cost.toFixed(2);
  }
  
  if (typeof cost === 'object') {
    // For objects like {1K: 12.74, 2K: 12.74, 4K: 21.84}
    // Show the minimum value
    const values = Object.values(cost);
    if (values.length > 0) {
      const minValue = Math.min(...values);
      return `from ${minValue.toFixed(2)}`;
    }
  }
  
  return 'N/A';
};

const FILTER_PILLS = [
  { id: 'google', label: 'Google', keywords: ['google'] },
  { id: 'reve', label: 'Reve', keywords: ['reve'] },
  { id: 'photo', label: 'Photorealistic', keywords: ['photorealistic', 'photo', 'real'] },
  { id: 'low_cost', label: 'Low Cost', isPriceFilter: true },
];

const AIModelSelector = ({ 
  selectedModelId = 'auto', 
  onModelSelect,
  contentType,  // "image" | "video" | "upscale"
  artFormat,     // "image" | "video"
  allowedModelIds  // Optional array of allowed model IDs to filter by
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [allModels, setAllModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activePill, setActivePill] = useState(null);
  const [sortOption, setSortOption] = useState('costAsc'); // 'featured', 'costAsc', 'vendor'
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);

  // Determine effective content type
  const effectiveContentType = (contentType || artFormat || 'image').toLowerCase();

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        setIsLoading(true);
        const specs = await loadAllModelSpecs();
        setAllModels(specs);
      } catch (error) {
        console.error('Failed to load model specs:', error);
        setAllModels([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadModels();
  }, []);

  // Lock body scroll when modal is open on mobile
  useEffect(() => {
    if (isModalOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(e.target);
      const isOutsideModal = modalRef.current && !modalRef.current.contains(e.target);

      if (isOutsideDropdown && isOutsideModal) {
        setIsModalOpen(false);
        setSearchQuery('');
      }
    };

    if (isModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isModalOpen]);

  // Filtering and Sorting Logic
  const processedModels = useMemo(() => {
    let result = [...allModels];

    // 0. Filter by Allowed Model IDs (if provided)
    if (Array.isArray(allowedModelIds) && allowedModelIds.length > 0) {
      result = result.filter(model => allowedModelIds.includes(model.id));
    }

    // 1. Filter by Content Type
    if (effectiveContentType) {
      result = result.filter(model => 
        model.function?.toLowerCase() === effectiveContentType
      );
    }

    // 2. Search Filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(model => 
        model.name?.toLowerCase().includes(query) ||
        model.vendor?.toLowerCase().includes(query) ||
        model.description?.toLowerCase().includes(query)
      );
    }

    // 3. Pill Filter
    if (activePill) {
      const pill = FILTER_PILLS.find(p => p.id === activePill);
      if (pill) {
        if (pill.isPriceFilter) {
          // Low Cost Filter: arbitrary threshold or relative?
          // Let's assume "Low Cost" means < 1 credit/unit or generally cheap.
          // Using model.cost (parsed numeric value)
          result = result.filter(m => (m.cost || 0) < 2); 
        } else {
          result = result.filter(m => {
            const text = `${m.vendor} ${m.name} ${m.description}`.toLowerCase();
            return pill.keywords.some(k => text.includes(k));
          });
        }
      }
    }

    // 4. Sorting
    result.sort((a, b) => {
      if (sortOption === 'vendor') {
        return (a.vendor || '').localeCompare(b.vendor || '');
      } else if (sortOption === 'costAsc') {
        // Cost Sort Logic based on User Request
        let costA = 0, costB = 0;
        
        if (effectiveContentType === 'video') {
          // Sort by spec.min_cost
          costA = a.min_cost ?? a.cost ?? 0;
          costB = b.min_cost ?? b.cost ?? 0;
        } else {
          // Sort by spec.unit_cost (Image/Upscale)
          costA = a.unit_cost ?? a.cost ?? 0;
          costB = b.unit_cost ?? b.cost ?? 0;
        }
        
        // Handle cases where unit_cost/min_cost might be an object (complex pricing)
        // Extract minimum value from object or fallback to cost
        if (typeof costA === 'object' && costA !== null) {
          const values = Object.values(costA);
          costA = values.length > 0 ? Math.min(...values) : (a.cost || 0);
        }
        if (typeof costB === 'object' && costB !== null) {
          const values = Object.values(costB);
          costB = values.length > 0 ? Math.min(...values) : (b.cost || 0);
        }

        return costA - costB;
      }
      return 0; // Featured/Default order
    });

    return result;
  }, [allModels, effectiveContentType, searchQuery, activePill, sortOption, allowedModelIds]);

  const selectedModel = allModels.find(m => m.id === selectedModelId) || allModels[0];

  const handleModelSelect = (modelId) => {
    if (onModelSelect) onModelSelect(modelId);
    setIsModalOpen(false);
    setSearchQuery('');
  };

  // Auto-select first valid model if current selection is invalid for the type
  useEffect(() => {
    if (!isLoading && allModels.length > 0 && effectiveContentType) {
      const currentValid = allModels.find(m => m.id === selectedModelId && m.function?.toLowerCase() === effectiveContentType);
      // If current model is not valid for the new content type, auto-select the first model from filtered list
      if (!currentValid && processedModels.length > 0) {
        if (onModelSelect) onModelSelect(processedModels[0].id);
      }
    }
  }, [effectiveContentType, processedModels, selectedModelId, onModelSelect, isLoading, allModels]);


  if (isLoading || !selectedModel) {
    return (
      <div className="h-10 w-48 bg-[#0f0f0f] rounded-lg animate-pulse border border-white/10" />
    );
  }

  return (
    <div className="relative min-w-0 flex-shrink-0" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsModalOpen(!isModalOpen)}
        className="group relative flex items-center gap-1.5 sm:gap-3 bg-[#0f0f0f] hover:bg-[#1a1a1a] border border-white/10 hover:border-blue-500/30 rounded-lg sm:rounded-xl px-2 sm:px-4 py-1.5 sm:py-2.5 transition-all duration-300 max-w-full"
      >
        <div className="relative flex-shrink-0">
            <img 
            src={selectedModel.logo || selectedModel.logoFallback || FALLBACK_LOGO} 
            alt={selectedModel.name}
            className="w-5 h-5 sm:w-6 sm:h-6 rounded-md object-contain"
            onError={(e) => { e.target.src = FALLBACK_LOGO; }}
            />
            <div className="absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 border-2 border-[#0f0f0f] rounded-full"></div>
        </div>
        
        <div className="flex flex-col items-start text-left min-w-0 flex-1 sm:mr-2">
            <span className="text-[9px] sm:text-xs text-gray-400 font-medium tracking-wide uppercase truncate max-w-full hidden sm:block">{selectedModel.vendor}</span>
            <span className="text-[10px] sm:text-sm text-white font-semibold tracking-tight truncate max-w-full">{selectedModel.name}</span>
        </div>

        <FaChevronDown className={`text-gray-500 text-[10px] sm:text-xs transition-transform duration-300 flex-shrink-0 ${isModalOpen ? 'rotate-180' : ''} group-hover:text-blue-400`} />
      </button>

      {/* Modal */}
      {createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <div 
              className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-end md:items-center md:justify-center md:p-4"
              onClick={(e) => {
                // Close modal when clicking backdrop (not the modal itself)
                if (e.target === e.currentTarget) {
                  setIsModalOpen(false);
                  setSearchQuery('');
                }
              }}
            >
              <motion.div 
                ref={modalRef}
                initial={{ opacity: 0, y: '100%' }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: '100%' }}
                transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                className="bg-[#09090b] w-full flex flex-col overflow-hidden shadow-2xl
                  h-[75vh] rounded-t-2xl border-t border-white/10
                  md:h-auto md:max-h-[90vh] md:border md:rounded-2xl md:max-w-4xl"
               >
                  {/* Modal Header */}
                  <div className="px-6 py-5 border-b border-white/10 bg-[#0f0f0f]/50 shrink-0">
                      <div className="flex items-center justify-between mb-4">
                          <div>
                              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                  Select AI Model
                              </h2>
                              <p className="text-sm text-gray-400 mt-1">Choose the perfect model for your {effectiveContentType} generation</p>
                          </div>
                          <button 
                              onClick={() => setIsModalOpen(false)}
                              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                          >
                              <FaTimes />
                          </button>
                      </div>

                      {/* Search and Controls Row */}
                      <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                              <input 
                                  type="text"
                                  placeholder="Search models, vendors..."
                                  value={searchQuery}
                                  onChange={(e) => setSearchQuery(e.target.value)}
                                  className="w-full pl-10 pr-4 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 outline-none transition-all"
                              />
                          </div>
                          
                          {/* Sort Dropdown */}
                          <div className="relative group shrink-0">
                              <select 
                                  value={sortOption}
                                  onChange={(e) => setSortOption(e.target.value)}
                                  className="appearance-none w-full sm:w-48 pl-10 pr-8 py-2.5 bg-[#141414] border border-white/10 rounded-xl text-sm text-white focus:ring-2 focus:ring-blue-500/20 outline-none cursor-pointer"
                              >
                                  <option value="featured">Featured</option>
                                  <option value="costAsc">Lowest Cost</option>
                                  <option value="vendor">Vendor (A-Z)</option>
                              </select>
                              <FaSortAmountDown className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                              <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none" />
                          </div>
                      </div>

                      {/* Tax Disclaimer Message */}
                      <p className="text-xs text-gray-500 mt-3 text-center sm:text-left">
                          All prices are excluding India tax (18% GST)
                      </p>

                      {/* Filter Pills (Commented out) */}
                      {/* <div className="flex flex-wrap gap-2 mt-4">
                          <button 
                              onClick={() => setActivePill(null)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                                  activePill === null 
                                  ? 'bg-blue-500/10 border-blue-500/50 text-blue-400' 
                                  : 'bg-[#141414] border-white/5 text-gray-400 hover:border-white/20'
                              }`}
                          >
                              All Models
                          </button>
                          {FILTER_PILLS.map(pill => (
                              <button
                                  key={pill.id}
                                  onClick={() => setActivePill(activePill === pill.id ? null : pill.id)}
                                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border flex items-center gap-1.5 ${
                                      activePill === pill.id 
                                      ? 'bg-blue-500/10 border-blue-500/50 text-blue-400' 
                                      : 'bg-[#141414] border-white/5 text-gray-400 hover:border-white/20'
                                  }`}
                              >
                                  {activePill === pill.id && <FaCheck className="text-[10px]" />}
                                  {pill.label}
                              </button>
                          ))}
                      </div> */}
                  </div>

                  {/* Models Grid */}
                  <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                      {processedModels.length === 0 ? (
                          <div className="text-center py-12">
                              <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
                                  <FaFilter className="text-gray-500 text-xl" />
                              </div>
                              <h3 className="text-lg font-medium text-white">No models found</h3>
                              <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or search query</p>
                              <button 
                                  onClick={() => {setSearchQuery(''); setActivePill(null);}}
                                  className="mt-4 text-blue-400 text-sm hover:underline"
                              >
                                  Clear all filters
                              </button>
                          </div>
                      ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {processedModels.map((model) => (
                                  <button
                                      key={model.id}
                                      onClick={() => handleModelSelect(model.id)}
                                      className={`relative group flex flex-col p-4 rounded-xl border text-left transition-all duration-200 ${
                                          selectedModelId === model.id
                                          ? 'bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.1)]'
                                          : 'bg-[#141414] border-white/5 hover:border-white/20 hover:bg-[#1a1a1a]'
                                      }`}
                                  >
                                      <div className="flex items-start gap-3 mb-3 w-full">
                                          <div className="w-12 h-12 rounded-lg bg-black/40 border border-white/10 p-1.5 shrink-0">
                                              <img 
                                                  src={model.logo || model.logoFallback || FALLBACK_LOGO} 
                                                  alt={model.name}
                                                  className="w-full h-full object-contain rounded"
                                                  onError={(e) => { e.target.src = FALLBACK_LOGO; }}
                                              />
                                          </div>
                                          <div className="min-w-0 flex-1 pt-0.5">
                                              <div className="flex items-center gap-2 justify-between w-full">
                                                  <h4 className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors truncate pr-2">
                                                      {model.name}
                                                  </h4>
                                                  {selectedModelId === model.id && (
                                                      <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-500/30 text-[10px] text-blue-300 font-medium">
                                                          Active
                                                      </span>
                                                  )}
                                              </div>
                                              <p className="text-xs text-gray-400 mt-0.5 truncate">{model.vendor}</p>
                                          </div>
                                      </div>
                                      
                                      {/* Price Badge - New Line for better visibility */}
                                      <div className="mb-3 -mx-1">
                                          <div className="inline-block px-2.5 py-1.5 mx-1 rounded-lg bg-[#0f0f0f] border border-white/10 group-hover:border-green-500/30 group-hover:bg-green-500/5 transition-all max-w-full">
                                               <span className="text-xs font-semibold text-green-400 block break-words leading-tight">
                                                   {model.price || 'Free'}
                                               </span>
                                          </div>
                                      </div>

                                      <p className="text-xs text-gray-400 leading-relaxed line-clamp-2 mb-3">
                                          {model.description}
                                      </p>

                                      {/* <div className="mt-auto pt-3 border-t border-white/5 flex items-center justify-between text-[10px] text-gray-500">
                                          <div className="flex items-center gap-2">
                                              <span className="flex items-center gap-1">
                                                  <FaTag className="text-[9px]" />
                                                  {model.function}
                                              </span>
                                          </div>

                                          {(sortOption === 'costAsc') && (
                                              <span className="font-mono opacity-70">
                                                  Ref: {effectiveContentType === 'video' 
                                                    ? formatCostForDisplay(model.min_cost) 
                                                    : formatCostForDisplay(model.unit_cost)}
                                              </span>
                                          )}
                                      </div> */}
                                  </button>
                              ))}
                          </div>
                      )}
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-white/10 bg-[#0f0f0f]/50 flex justify-between items-center text-xs text-gray-500 shrink-0">
                      <span>Showing {processedModels.length} model{processedModels.length !== 1 ? 's' : ''}</span>
                      <div className="flex gap-4">
                          <span className="hidden md:inline">Press ESC to close</span>
                      </div>
                  </div>
               </motion.div>
            </div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default AIModelSelector;
